// src/components/seo/GlobalSeoHead.jsx
// Injects site-wide <head> tags that don't belong to any single page:
//   • RSS <link rel="alternate"> tags (main feed + per-category)
//   • Google Search Console / Bing verification meta
//   • Organization + WebSite JSON-LD (entity signal for all pages)
//
// Mounts once at the App root level. Uses react-helmet-async.
// Verification codes are loaded from platform_settings on first mount.

import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "../../lib/supabaseClient";
import { resolveIdentity } from "../../seo/identityEngine.js";
import JsonLd from "./JsonLd";

const SITE_URL = "https://luxuryweddingdirectory.com";
// Site-wide default og/twitter title = the homepage identity, owned by the engine.
const HOME_SEO = resolveIdentity("/");

// Organization + WebSite schemas, injected on every page for entity recognition
const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Luxury Wedding Directory",
  alternateName: "LWD",
  url: SITE_URL,
  logo: `${SITE_URL}/lwd-logo.png`,
  sameAs: [
    "https://www.instagram.com/luxuryweddingdirectory",
    "https://www.pinterest.com/luxuryweddingdirectory",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    url: `${SITE_URL}/contact`,
  },
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Luxury Wedding Directory",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
};

// RSS feeds, hardcoded (stable, no DB lookup needed)
const RSS_FEEDS = [
  { title: "Luxury Wedding Directory Magazine",               href: `${SITE_URL}/feed.xml` },
  { title: "Luxury Wedding Directory: Destinations",         href: `${SITE_URL}/magazine/destinations/feed.xml` },
  { title: "Luxury Wedding Directory: Real Weddings",        href: `${SITE_URL}/magazine/real-weddings/feed.xml` },
  { title: "Luxury Wedding Directory: Venues",               href: `${SITE_URL}/magazine/venues/feed.xml` },
  { title: "Luxury Wedding Directory: Style & Beauty",       href: `${SITE_URL}/magazine/style-beauty/feed.xml` },
  { title: "Luxury Wedding Directory: Planning Intelligence",href: `${SITE_URL}/magazine/planning/feed.xml` },
  { title: "Luxury Wedding Directory: Food & Drink",         href: `${SITE_URL}/magazine/food-drink/feed.xml` },
];

export default function GlobalSeoHead() {
  const [googleCode, setGoogleCode] = useState("");
  const [bingCode,   setBingCode]   = useState("");

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .in("key", ["seo_google_verification", "seo_bing_verification"]);

      (data || []).forEach(row => {
        if (row.key === "seo_google_verification") setGoogleCode(row.value || "");
        if (row.key === "seo_bing_verification")   setBingCode(row.value || "");
      });
    }
    load();
  }, []);

  return (
    <>
    <JsonLd schema={ORG_SCHEMA} />
    <JsonLd schema={WEBSITE_SCHEMA} />
    <Helmet>
      {/* ── Default title/description/og/twitter, site-wide fallback ───
          Rendered on every page (this component mounts once at the App
          root), so any page's own <Helmet> with these same tag names
          correctly replaces them via Helmet's own dedup, unlike a static
          tag baked into index.html, which Helmet cannot clean up. Keeps
          the previous copy verbatim for pages that don't set their own. */}
      <meta name="description" content="Discover the world's finest luxury wedding venues, planners and vendors. Handpicked by LWD for couples planning exceptional weddings." />
      <meta property="og:title" content={HOME_SEO.title} />
      <meta property="og:description" content="Discover the world's finest luxury wedding venues, planners and vendors. Handpicked for couples planning exceptional weddings." />
      <meta property="og:url" content={SITE_URL + "/"} />
      <meta property="og:image" content={`${SITE_URL}/og-default.jpg`} />
      <meta name="twitter:title" content={HOME_SEO.title} />
      <meta name="twitter:description" content="Discover the world's finest luxury wedding venues, planners and vendors." />
      <meta name="twitter:image" content={`${SITE_URL}/og-default.jpg`} />

      {/* ── RSS auto-discovery ─────────────────────────────────────────── */}
      {RSS_FEEDS.map(feed => (
        <link
          key={feed.href}
          rel="alternate"
          type="application/rss+xml"
          title={feed.title}
          href={feed.href}
        />
      ))}

      {/* ── Search console verification ───────────────────────────────── */}
      {googleCode && (
        <meta name="google-site-verification" content={googleCode} />
      )}
      {bingCode && (
        <meta name="msvalidate.01" content={bingCode} />
      )}
    </Helmet>
    </>
  );
}
